import { z } from 'zod';

/**
 * A play type's UI capabilities (U5, F6 — registry consolidation), sourced
 * from `PlayConnectorSpec.ui` (`src/services/flow/play-connectors.ts`). Wire
 * mirror of that block — client code reads these instead of comparing
 * `play_type_slug` against `GONG_FLOW_PLAY_TYPE_SLUG` (U5B, a separate
 * client-side unit), so a future second rich/own-section play type behaves
 * correctly with no new client-side slug branch.
 */
export const PlayUiCapabilitiesSchema = z.object({
  label: z.string(),
  own_picker_section: z.boolean(),
  has_step_overrides: z.boolean(),
  body_format: z.enum(['plain', 'rich']),
}).strict();
export type PlayUiCapabilities = z.infer<typeof PlayUiCapabilitiesSchema>;

/**
 * Flow Queuing System (v0) — play catalog wire schemas (U4).
 *
 * `GET /api/flow/queue/plays` backs the picker's inline search/assign list.
 * Pre-#1062 (Play Manager not yet merged — see
 * docs/flow/flow-queuing-implementation-plan.md "Dependencies & sequencing")
 * this is served by a static stub (`src/services/flow/play-catalog.ts`)
 * seeded from the locked prototype's live plays; once #1062 lands, the same
 * `PlayCatalog` interface swaps to `deriveLivePlays` with no wire-contract
 * change.
 *
 * Checkpoint decision (2026-07-21, plans/flow-queue-v0/notes/…72x.4.md):
 * NO suggested-first ordering in v0 — plays list in plain catalog order
 * (play-type groups in catalog order, plays in declaration order within a
 * group). There is deliberately no `suggested` field on `PlayOptionSchema`.
 */

/**
 * One assignable play in the picker. Only LIVE play types are exposed here.
 * invite_subject/invite_body = the play's campaign copy (DerivedPlay) — the
 * editor's prefill seed (resolveCopy semantics: rep input > campaign).
 */
export const PlayOptionSchema = z.object({
  play_type_id: z.number(),
  play_instance_ref: z.string(),
  name: z.string(),
  play_type_name: z.string(),
  play_type_slug: z.string(),
  invite_subject: z.string().nullable(),
  invite_body: z.string().nullable(),
  /**
   * Does this play type have rep-editable per-lead copy (Subject/Copy in the
   * queue editor)? Derived from the connector's inputSchema: true iff any field
   * is sourced from `input.*` (event_invite → true; generate_message, which
   * resolves everything from the contact/system, → false, so the editor hides
   * the copy fields). The per-play form is config, driven by the input schema.
   */
  has_editable_copy: z.boolean(),
  /**
   * Does this play's send REQUIRE the contact's email? Derived from the
   * connector's inputSchema: true iff a REQUIRED field is sourced from
   * `contact.email` (event_invite, generate_message → true; warm_intro's
   * 'Contact Email' is optional → false). Server-enforced at add
   * (addQueueItem) and assign (patchQueueItem) — this flag exists so the
   * picker never OFFERS a play the write would reject for a null-email item.
   */
  requires_email: z.boolean(),
  /**
   * Does this play type's rep-editable Subject/Copy field REQUIRE a value
   * at send time, with no blank-falls-back-to-template semantics? Derived
   * from the connector's inputSchema: true iff a REQUIRED field is sourced
   * from `input.*`
   * (event_invite's `subject`/`email_copy` are both `required: true` →
   * true). `warm_intro`'s `Subject`/`Body` are ALSO `input.*`-sourced (that's
   * what flips `has_editable_copy` to true for it) but carry no `required`
   * key at all — a blank draft legitimately falls back to a server-rendered
   * template at send time (see `warm-intro-definition.ts`), so this is
   * `false` for warm_intro even though `has_editable_copy` is `true`.
   * `gong_flow`/`generate_message` render neither field (`has_editable_copy`
   * is `false` for both), so this is `false` for them too — it is only ever
   * meaningful alongside `has_editable_copy: true`. Used by the queue
   * editor's Subject/Copy labels to show a required-asterisk only where the
   * field is genuinely required to send, never on a field that's allowed to
   * stay blank.
   *
   * `.optional()` (same backward-compatibility rationale as
   * `SendersResponseSchema.default_sender`'s doc comment) — this is an
   * ADDITIVE field onto an already-`.strict()` schema with many existing
   * fixtures/mocks across the test suite built against the pre-existing
   * shape; `.optional()` lets those keep parsing rather than failing outright
   * the instant this field landed. Every live producer sets a real boolean;
   * read sites treat an absent value as "not required" (`?? false`), the
   * same fail-closed default `has_editable_copy`'s own missing-capability
   * fallback uses.
   */
  copy_required: z.boolean().optional(),
  /**
   * This play type's UI capabilities (U5, F6) — see `PlayUiCapabilitiesSchema`.
   * Required — the producer (`play-catalog.ts`) always sets it, and the only
   * client construction site (`PlayPicker.tsx`'s `buildGongFlowPlayOption`)
   * carries it through.
   */
  ui: PlayUiCapabilitiesSchema,
}).strict();
export type PlayOption = z.infer<typeof PlayOptionSchema>;

/**
 * One LIVE (active + enabled-connector) play TYPE, independent of whether
 * it currently has any derived instances (PR #1312 review §7-3 / plan
 * `2026-08-22-001` R5e — the "kind marker" fix for the picker bootstrap
 * trap: `gong_flow`'s exposure deriver returns `[]` on an empty mirror, so
 * scanning `plays` for a gong_flow row never finds one on a fresh install,
 * and the picker's "Browse all Gong flows →" affordance — the only way to
 * populate that mirror — never renders, deadlocking the bootstrap).
 *
 * `.pick()` off `PlayOptionSchema` (never hand-duplicated — `terra/no-
 * duplicate-schema-name`) since every field here is also a `PlayOption`
 * field; the ones a per-instance row additionally carries
 * (`play_instance_ref`, `name`, `invite_subject`, `invite_body`) are
 * deliberately absent — this marker exists ONLY to prove a type is live and
 * to carry its type-wide (not per-instance) capability flags.
 */
export const ActivePlayTypeSchema = PlayOptionSchema.pick({
  play_type_id: true,
  play_type_slug: true,
  play_type_name: true,
  has_editable_copy: true,
  requires_email: true,
  copy_required: true,
  ui: true,
}).strict();
export type ActivePlayType = z.infer<typeof ActivePlayTypeSchema>;

/**
 * GET /api/flow/queue/plays — the live play catalog, in catalog order, plus
 * `active_play_types` (R5e, above). `active_play_types` is REQUIRED, not
 * optional: Terra deploys FE+BE atomically in one image, so a stale browser
 * tab running the old bundle against the new backend failing this
 * `.strict()` parse is accepted, documented exposure (plan `2026-08-22-001`
 * Risks #2) — do NOT loosen `.strict()` or make this field optional to
 * paper over that case.
 */
export const PlayCatalogResponseSchema = z.object({
  plays: z.array(PlayOptionSchema),
  active_play_types: z.array(ActivePlayTypeSchema),
}).strict();
export type PlayCatalogResponse = z.infer<typeof PlayCatalogResponseSchema>;
